import { HYSA_SLUG, SLUGS_WITH_MONETIZABLE_FILTER } from './constants';

import { MonetizableEnum } from '@repo/supergraph/marketplace/enums';
import type { CategorySection, ValidatedRoundupPage } from '@repo/supergraph/roundup/base/types';

export const filterByMonetizable = <
  T extends {
    marketplaceEntity: { monetizable?: MonetizableEnum };
    showEditorialPickAsProductCard?: boolean;
  },
>(
  items: T[],
  slug?: string,
  /**
   * If true, skip ALL filtering (HYSA redesign experiment treatment shows all offers)
   */
  isHysaRedesignTreatment?: boolean,
): T[] => {
  // For HYSA redesign treatment, show all items without any filtering
  if (isHysaRedesignTreatment) {
    return items;
  }

  const filtered = items.filter(item => {
    const isMonetizable = item.marketplaceEntity.monetizable === MonetizableEnum.yesAssumed;

    // Only apply showEditorialPickAsProductCard filtering for HYSA (when not in treatment)
    const shouldCheckEditorialPick = slug === HYSA_SLUG;
    const shouldHide = shouldCheckEditorialPick ? item.showEditorialPickAsProductCard : false;

    return isMonetizable && !shouldHide;
  });

  return filtered;
};

export const filterCategorySectionsByMonetizable = (
  categorySections: CategorySection[],
  slug?: string,
  isHysaRedesignTreatment?: boolean,
) => {
  return categorySections
    .map(section => ({
      ...section,
      productCards: filterByMonetizable(section.productCards, slug, isHysaRedesignTreatment),
    }))
    .filter(section => section.productCards.length > 0);
};

export const filterRoundupOffersByMonetizable = <T extends ValidatedRoundupPage>(
  roundup: T,
  isHysaRedesignTreatment?: boolean,
): T => {
  if (!SLUGS_WITH_MONETIZABLE_FILTER.includes(roundup.slug)) {
    return roundup;
  }

  const filteredRows = filterByMonetizable(
    roundup.summaryTableSection.rows,
    roundup.slug,
    isHysaRedesignTreatment,
  );
  const filteredCategorySections = filterCategorySectionsByMonetizable(
    roundup.productCardsSection.categorySections,
    roundup.slug,
    isHysaRedesignTreatment,
  );

  const filteredRoundup: T = {
    ...roundup,
    summaryTableSection: { ...roundup.summaryTableSection, rows: filteredRows },
    productCardsSection: {
      ...roundup.productCardsSection,
      categorySections: filteredCategorySections,
    },
  };

  if (roundup.recapListSection) {
    const filteredRecapItems = filterByMonetizable(
      roundup.recapListSection.recapItems ?? [],
      roundup.slug,
      isHysaRedesignTreatment,
    );
    filteredRoundup.recapListSection = {
      ...roundup.recapListSection,
      recapItems: filteredRecapItems,
    };
  }

  return filteredRoundup;
};
